import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

const styles = {
  page: {
    minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#0F172A', fontFamily: 'system-ui, sans-serif',
  },
  card: {
    background: '#1E293B', borderRadius: 14, padding: '2rem', width: '100%', maxWidth: 380,
    boxShadow: '0 4px 32px rgba(0,0,0,0.25)', border: '1px solid #334155',
  },
  title: { margin: '0 0 0.5rem', fontSize: '1.4rem', fontWeight: 700, color: '#F8FAFC' },
  subtitle: { margin: '0 0 1.5rem', fontSize: '0.9rem', color: '#94A3B8' },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#94A3B8', marginBottom: 4 },
  input: {
    width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1.5px solid #334155',
    fontSize: '1rem', boxSizing: 'border-box', outline: 'none', marginBottom: '1rem',
    background: '#1E293B', color: '#F8FAFC',
  },
  btn: {
    width: '100%', padding: '0.75rem', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff', fontSize: '1rem', fontWeight: 700,
    cursor: 'pointer', marginTop: 4,
  },
  error: { color: '#F87171', fontSize: '0.875rem', marginBottom: '1rem' },
  footer: { textAlign: 'center', marginTop: '1.25rem', fontSize: '0.85rem', color: '#94A3B8' },
  link: { color: '#7C3AED', textDecoration: 'none', fontWeight: 600 },
  legal: {
    marginTop: '0.85rem', fontSize: '0.75rem', color: '#64748B', textAlign: 'center', lineHeight: 1.5,
  },
};

export default function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const referralCode = useMemo(() => {
    const raw = searchParams.get('ref') || '';
    return raw.trim().slice(0, 32);
  }, [searchParams]);

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const showEmailError = emailTouched && email && !isValidEmail(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) { setError(t('signup.emailError')); return; }
    if (password.length < 8) { setError(t('signup.minLength')); return; }
    if (password !== confirm) { setError(t('signup.mismatch')); return; }
    setLoading(true);
    setError(null);
    try {
      const body = { email, password, ...(referralCode ? { referralCode } : {}) };
      const res = await fetch(`${API_BASE}/api/v1/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || data.error || t('signup.failed'));
        return;
      }
      const data = await res.json();
      login(data.accessToken, data.refreshToken, true);
      navigate('/admin/welcome', { replace: true });
    } catch {
      setError(t('signup.networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <Link to="/" style={{ textDecoration: 'none', display: 'block', textAlign: 'center', marginBottom: '1rem' }}>
          <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#7C3AED' }}>vQueue</span>
        </Link>

        <h1 style={styles.title}>{t('signup.title')}</h1>
        <p style={styles.subtitle}>{t('signup.subtitleOneStep')}</p>

        {referralCode && (
          <p style={{
            background: 'rgba(124,58,237,0.12)', color: '#A78BFA',
            padding: '0.6rem 0.75rem', borderRadius: 8, fontSize: '0.85rem',
            marginBottom: '1rem', border: '1px solid rgba(124,58,237,0.3)',
          }}>
            {t('signup.referralApplied', { code: referralCode })}
          </p>
        )}
        {error && <p style={styles.error}>{error}</p>}

        <form onSubmit={handleSubmit}>
          <label style={styles.label}>{t('signup.emailLabel')}</label>
          <input
            style={{ ...styles.input, ...(showEmailError ? { borderColor: '#EF4444' } : {}) }}
            type="email"
            placeholder={t('signup.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            autoComplete="email"
            required
          />
          {showEmailError && <p style={{ color: '#EF4444', fontSize: '0.8rem', margin: '-0.75rem 0 0.75rem' }}>{t('signup.emailError')}</p>}

          <label style={styles.label}>{t('signup.passwordLabel')}</label>
          <div style={{ position: 'relative' }}>
            <input
              style={{ ...styles.input, paddingRight: '3.5rem' }}
              type={showPassword ? 'text' : 'password'}
              placeholder={t('signup.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              style={{
                position: 'absolute', right: 10, top: 9, background: 'none', border: 'none',
                color: '#94A3B8', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {showPassword ? t('signup.hide') : t('signup.show')}
            </button>
          </div>

          <label style={styles.label}>{t('signup.confirmLabel')}</label>
          <input
            style={styles.input}
            type={showPassword ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />

          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? t('signup.submitting') : t('signup.createAccount')}
          </button>

          <p style={styles.legal}>
            {t('signup.legalPrefix')}{' '}
            <Link to="/terms" style={styles.link}>{t('signup.termsLink')}</Link>
            {' '}{t('signup.legalAnd')}{' '}
            <Link to="/privacy" style={styles.link}>{t('signup.privacyLink')}</Link>.
          </p>
        </form>
        <p style={styles.footer}>
          {t('signup.hasAccount')} <Link to="/admin/login" style={styles.link}>{t('signup.signIn')}</Link>
        </p>
      </div>
    </div>
  );
}
